import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { auth, functions } from '../firebase';
import { GoogleAuthProvider, signInWithPopup, signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import LanguageSelector from '../components/LanguageSelector';
import { claveDeErrorDeGoogle, correoDelError } from '../utils/errorDeGoogle';
import { verDestino } from '../utils/destinoTrasLogin';
import { intencionDeAcceso } from '../utils/intencionDeAcceso';
import { getAttribution } from '../utils/attribution';
import { planLimits } from '../utils/planHelpers';
import { ESPERA_REENVIO_MS, segundosParaReenviar } from '../utils/esperaDeReenvio';

const NOMBRE_DE_PLAN = { starter: 'Starter', pro: 'Pro' };

const Login = () => {
  const { t } = useTranslation('common');
  // ¿Viene de un «Comienza ahora» de la landing? Se lee UNA vez y sin consumir el destino: lo
  // consume `RedirigirTrasLogin` después de entrar. Ver `utils/intencionDeAcceso.js` (C6a).
  const [intencion] = useState(() => intencionDeAcceso({ ruta: verDestino(), atribucion: getAttribution() }));
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reenvío con espera (C6b): por marca de tiempo, ver `utils/esperaDeReenvio.js`.
  const [reenvioDisponibleEn, setReenvioDisponibleEn] = useState(0);
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    if (!codeSent) return undefined;
    const id = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(id);
  }, [codeSent]);
  const segundosReenvio = segundosParaReenviar(reenvioDisponibleEn, ahora);
  const empezarEsperaDeReenvio = () => {
    const t0 = Date.now();
    setAhora(t0);
    setReenvioDisponibleEn(t0 + ESPERA_REENVIO_MS);
  };

  const sendVerificationCode = httpsCallable(functions, 'sendVerificationCode', { timeout: 30000 });
  const verifyCode = httpsCallable(functions, 'verifyCode', { timeout: 30000 });

  const handleResendCode = async () => {
    if (loading || segundosReenvio > 0) return;
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await sendVerificationCode({ email });
      setCode('');
      empezarEsperaDeReenvio();
      setMessage(t('login.codeResent'));
    } catch (err) {
      console.error('Error al reenviar código:', err);
      // El tope del servidor (5 envíos / 15 min por correo) llega como resource-exhausted.
      setError(t(err?.code === 'functions/resource-exhausted' ? 'login.errorTooManyAttempts' : 'login.errorSendCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      await sendVerificationCode({ email });
      setCodeSent(true);
      empezarEsperaDeReenvio();
      setMessage(t('login.codeSentMessage'));
    } catch (err) {
      console.error('Error al enviar código:', err);
      setError(t('login.errorSendCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const result = await verifyCode({ email, code });
      const { customToken } = result.data;

      // Autenticar con el custom token
      await signInWithCustomToken(auth, customToken);
      setMessage(t('login.authSuccess'));

      // A `/`, NO a `/dashboard`: en `/` está `RedirigirTrasLogin`, que consume el destino
      // guardado (p. ej. `/contratar?plan=starter` desde la landing) y, si no hay ninguno, manda
      // a `/dashboard` igual que antes. Ir directo a `/dashboard` se saltaba el destino y el
      // código por correo perdía el plan elegido; Google ya pasaba por `/`.
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } catch (err) {
      console.error('Error al verificar código:', err);

      // Manejar errores específicos
      if (err.code === 'functions/not-found') {
        setError(t('login.errorCodeNotFound'));
      } else if (err.code === 'functions/deadline-exceeded') {
        setError(t('login.errorCodeExpired'));
      } else if (err.code === 'functions/permission-denied') {
        setError(t('login.errorCodeIncorrect'));
      } else if (err.code === 'functions/resource-exhausted') {
        setError(t('login.errorTooManyAttempts'));
      } else {
        setError(t('login.errorVerifyCode'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      padding: '2rem',
      maxWidth: '400px',
      margin: '0 auto',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <LanguageSelector />
      </div>
      {intencion.contratar ? (
        <>
          <h2 style={{ textAlign: 'center', color: '#667eea', marginBottom: '0.75rem' }}>{t('login.titleContratar')}</h2>
          <p style={{ textAlign: 'center', color: '#555', fontSize: '15px', lineHeight: 1.5, margin: '0 0 1rem 0' }}>
            {t('login.codeExplanation')}
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap',
            backgroundColor: '#f4f5fe',
            border: '1px solid #dfe2fb',
            borderRadius: '8px',
            padding: '10px 14px',
            marginBottom: '2rem',
            fontSize: '14px',
          }}>
            <span style={{ color: '#333' }}>
              {t(intencion.elegido ? 'login.planSummary' : 'login.planSummarySugerido', { plan: NOMBRE_DE_PLAN[intencion.plan], price: planLimits[intencion.plan].price })}
            </span>
            <a href="https://pux.app/#precios" style={{ color: '#667eea', fontWeight: 500 }}>{t('login.changePlan')}</a>
          </div>
        </>
      ) : (
        <h2 style={{ textAlign: 'center', color: '#667eea', marginBottom: '2rem' }}>{t('login.title')}</h2>
      )}

      {/* Google Sign-In */}
      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={async () => {
            setError('');
            setMessage('');
            try {
              const provider = new GoogleAuthProvider();
              await signInWithPopup(auth, provider);
            } catch (e) {
              console.error('Error Google Sign-In', e?.code || e);
              // El texto depende del código: cancelar no es un error, una cuenta creada con el
              // código por correo tiene su propia salida. Ver `utils/errorDeGoogle.js`.
              const clave = claveDeErrorDeGoogle(e);
              if (!clave) return;
              const correo = correoDelError(e);
              if (clave === 'login.googleAccountExists' && correo && !codeSent) setEmail(correo);
              setError(t(clave));
            }
          }}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: '#4285f4',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 500,
            cursor: 'pointer',
            marginBottom: '1rem'
          }}
        >
          {t('login.googleSignIn')}
        </button>
        <div style={{ textAlign: 'center', color: '#666', margin: '1rem 0' }}>{t('or')}</div>
      </div>

      {/* Email Code Authentication */}
      {!codeSent ? (
        <form onSubmit={handleSendCode}>
          <input
            type="email"
            placeholder={t('login.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              marginBottom: '1rem',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '16px',
              boxSizing: 'border-box'
            }}
          />
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: loading ? '#ccc' : '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? t('login.sendingCode') : t('login.sendCode')}
          </button>
        </form>
      ) : (
        <div>
          <div style={{
            backgroundColor: '#f5f5f5',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '14px', color: '#666' }}>
              {t('login.codeSentTo')}
            </p>
            <p style={{ margin: 0, fontSize: '16px', fontWeight: 500, color: '#333' }}>
              {email}
            </p>
            <p style={{ margin: '0.75rem 0 0 0', fontSize: '13px', color: '#666' }}>
              {t('login.checkSpam')}
            </p>
          </div>

          <form onSubmit={handleVerifyCode}>
            <input
              type="text"
              placeholder={t('login.codePlaceholder')}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              disabled={loading}
              maxLength={6}
              style={{
                width: '100%',
                padding: '12px',
                marginBottom: '1rem',
                border: '2px solid #667eea',
                borderRadius: '8px',
                fontSize: '24px',
                fontWeight: 'bold',
                textAlign: 'center',
                letterSpacing: '8px',
                boxSizing: 'border-box'
              }}
            />
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: (loading || code.length !== 6) ? '#ccc' : '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 500,
                cursor: (loading || code.length !== 6) ? 'not-allowed' : 'pointer',
                marginBottom: '1rem'
              }}
            >
              {loading ? t('login.verifying') : t('login.verifyCode')}
            </button>
          </form>

          <button
            type="button"
            onClick={handleResendCode}
            disabled={loading || segundosReenvio > 0}
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: 'transparent',
              color: (loading || segundosReenvio > 0) ? '#999' : '#667eea',
              border: 'none',
              fontSize: '14px',
              cursor: (loading || segundosReenvio > 0) ? 'not-allowed' : 'pointer',
              textDecoration: (loading || segundosReenvio > 0) ? 'none' : 'underline'
            }}
          >
            {segundosReenvio > 0 ? t('login.resendIn', { segundos: segundosReenvio }) : t('login.resendCode')}
          </button>

          {/* Esto NO reenvía: vuelve a pedir el correo. Antes se llamaba «Solicitar un nuevo
              código», y quien lo pulsaba esperando un reenvío tenía que escribir el correo otra vez. */}
          <button
            type="button"
            onClick={() => {
              setCodeSent(false);
              setCode('');
              setError('');
              setMessage('');
            }}
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: 'transparent',
              color: '#667eea',
              border: 'none',
              fontSize: '14px',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            {t('login.useOtherEmail')}
          </button>
        </div>
      )}

      {/* Messages */}
      {message && (
        <div style={{
          marginTop: '1rem',
          padding: '12px',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '8px',
          color: '#155724',
          fontSize: '14px'
        }}>
          {message}
        </div>
      )}

      {error && (
        <div style={{
          marginTop: '1rem',
          padding: '12px',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '8px',
          color: '#721c24',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default Login;